import React, { useEffect, useState, useRef } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash } from "lucide-react";

import Button from "@/components/common/Button";
import FormField from "@/components/common/FormField";
import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { showToastNotification } from "@/utility/helper";
import { errorTypes } from "@/utility/validation";
import { IAddProductForm, IProductAddon, ISingleProductResponse } from "@/interfaces/Product/product.interface";
import { ICategoryResponse, ICategory } from "@/interfaces/Category/category.interface";
import { Switch } from "@/components/ui/switch";
import MultiImageUploader from "@/components/common/MultiImageUploader";
import { MultiSelect } from "@/components/common/MultiSelect";
import { AddTag } from "../Tags/AddTag";
import { ITag, ITagResponse } from "@/interfaces/Tag/tag.interface";
import { Label } from "@/components/ui/label";

export const AddProduct = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = Boolean(id);

    const [categories, setCategories] = useState<ICategory[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingProduct, setLoadingProduct] = useState(false);

    const [deletedMediaIds, setDeletedMediaIds] = useState<string[]>([]);
    const [deletedAddonIds, setDeletedAddonIds] = useState<string[]>([]);
    const [mediaMap, setMediaMap] = useState<Record<string, string>>({}); // URL -> ID
    const [tagSearch, setTagSearch] = useState('');
    const [tags, setTags] = useState<ITag[]>([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [showAddTagModal, setShowAddTagModal] = useState(false);

    const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<IAddProductForm & { images: (File | string)[] }>({
        defaultValues: {
            status: 'active',
            productAddons: [],
            images: [],
            subCategoryId: '',
            tagIds: [],
            vatStatus: 'inactive',
        }
    });

    const categoryId = watch('categoryId');

    const subCategoryOptions = React.useMemo(() => {
        const selectedCategory = categories.find(cat => cat.id === categoryId);
        if (selectedCategory && selectedCategory.subcategories) {
            return selectedCategory.subcategories.map(sub => ({
                label: sub.name,
                value: sub.id
            }));
        }
        return [];
    }, [categoryId, categories]);

    const tagOptions = React.useMemo(() => {
        return tags.map(tag => ({
            label: tag.name,
            value: tag.id
        }));
    }, [tags]);

    const { fields, append, remove } = useFieldArray({
        control,
        name: "productAddons"
    });

    // Custom remove function to track addon deletions
    const handleRemoveAddon = (index: number) => {
        const addonToRemove = fields[index];
        if (addonToRemove.id) {
            setDeletedAddonIds(prev => [...prev, addonToRemove.itemId!]);
        }
        remove(index);
    };


    const handleTagSearch = (value: string) => {
        setTagSearch(value);
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
        searchTimerRef.current = setTimeout(() => {
            fetchTags(value);
        }, 500);
    };

    useEffect(() => {
        const fetchCategories = async () => {
            setLoadingCategories(true);
            try {
                const response = await callApi<ICategoryResponse>(
                    `${API_URL + API_ENDPOINT.categories}?limit=100&status=active`,
                    { method: 'GET' }
                );
                setCategories(response?.data?.categories || []);
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                setLoadingCategories(false);
            }
        };
        fetchTags();
        fetchCategories();
    }, []);

    // Fetch product details for edit mode
    useEffect(() => {
        if (isEditMode && id) {
            const fetchProductDetails = async () => {
                setLoadingProduct(true);
                try {
                    const response = await callApi<ISingleProductResponse>(
                        `${API_URL + API_ENDPOINT.products}/${id}`,
                        { method: 'GET' }
                    );
                    const product = response?.data;

                    // Store media mapping
                    const mapping: Record<string, string> = {};
                    const imageUrls = product.media?.map(m => {
                        const url = API_URL + "/" + m.url;
                        mapping[url] = m.id;
                        return url;
                    }) || [];
                    setMediaMap(mapping);

                    // Map product details to form fields
                    reset({
                        name: product.name,
                        price: Number(product.price),
                        categoryId: product.categoryId,
                        subCategoryId: product.subCategoryId,
                        stockQuantity: product.productInfo?.stockQuantity || 0,
                        additionalInfo: product.productInfo?.additionalInfo || '',
                        descriptionLong: product.productInfo?.descriptionLong || '',
                        status: product.status as 'active' | 'inactive',
                        vatStatus: product.vatStatus,
                        productAddons: product.addons?.map(addon => ({
                            itemId: addon.id,
                            name: addon.name,
                            price: Number(addon.price),
                            inventory: Number(addon.inventory) || 1,
                            isCounter: addon.isCounter,
                            status: addon.status as 'active' | 'inactive'
                        })) || [],
                        images: imageUrls,
                        tagIds: product.productTags?.map(t => t.tagId) || []
                    });
                } catch (error) {
                    console.error("Error fetching product details:", error);
                    ThrowError(error);
                } finally {
                    setLoadingProduct(false);
                }
            };
            fetchProductDetails();
        }
    }, [id, isEditMode, reset]);



    const fetchTags = async (search = '') => {
        setLoadingTags(true);
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('limit', '100');
            if (search) queryParams.append('search', search);

            const response = await callApi<ITagResponse>(
                `${API_URL + API_ENDPOINT.tags}?${queryParams.toString()}`,
                { method: 'GET' }
            );
            setTags(response?.data?.tags || []);
        } catch (error) {
            console.error("Error fetching tags:", error);
        } finally {
            setLoadingTags(false);
        }
    };



    const onSubmit = async (data: IAddProductForm & { images: (File | string)[] }) => {
        try {
            const formData = new FormData();

            // Basic Fields
            formData.append('name', data.name?.trim());
            formData.append('price', String(Number(data.price)));
            formData.append('categoryId', data.categoryId);
            if (data.subCategoryId) {
                formData.append('subCategoryId', data.subCategoryId);
            }
            formData.append('stockQuantity', String(Number(data.stockQuantity)));
            formData.append('status', data.status);
            formData.append('vatStatus', data.vatStatus);
            formData.append('descriptionLong', data.descriptionLong?.trim());

            if (data.additionalInfo) {
                formData.append('additionalInfo', data.additionalInfo);
            }

            const formattedAddons: IProductAddon[] = data.productAddons.map(addon => ({
                ...addon,
                price: Number(addon.price),
                inventory: Number(addon.inventory) || 1,
                isCounter: String(addon.isCounter) === 'true'
            }));
            console.log(data);
            if (formattedAddons.length > 0) {
                formData.append('productAddons', JSON.stringify(formattedAddons));
            }

            if (data.tagIds && data.tagIds.length > 0) {
                formData.append('tagIds', JSON.stringify(data.tagIds));
            }

            if (data.images && data.images.length > 0) {
                data.images.forEach((img) => {
                    if (typeof img !== 'string') {
                        formData.append('files', img);
                    }
                });
            }

            // Append deleted IDs if in edit mode
            if (isEditMode && deletedMediaIds.length > 0) {
                formData.append('deleteMedia', JSON.stringify(deletedMediaIds));
            }
            if (isEditMode && deletedAddonIds.length > 0) {
                formData.append('deleteAddons', JSON.stringify(deletedAddonIds));
            }

            const url = isEditMode
                ? `${API_URL + API_ENDPOINT.products}/${id}`
                : `${API_URL + API_ENDPOINT.products}`;

            const method = isEditMode ? 'PATCH' : 'POST';

            const response = await callApi(
                url,
                {
                    method: method,
                    multipart: true,
                    data: formData,
                }
            );

            showToastNotification('success', response?.message || `Product ${isEditMode ? 'updated' : 'created'} successfully`);
            navigate(-1);
        } catch (error) {
            ThrowError(error);
        }
    };

    const categoryOptions = categories.map(cat => ({
        label: cat.name,
        value: cat.id
    }));

    if (loadingProduct) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <MainHeading
                title={isEditMode ? "Edit Product" : "Add Products"}
                showBack
                onBack={() => navigate(-1)}
            />

            <div className="bg-card rounded-lg border p-6 shadow-sm mb-6 ">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    {/* ── Basic Info ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <FormField
                                label="Product Name"
                                name="name"
                                type="text"
                                requiredIcon
                                placeholder="e.g. Eggs, Omlette"
                                error={errors.name?.message}
                                {...register('name', { ...errorTypes.required('Product Name') })}
                            />
                            <FormField
                                label="Price"
                                name="price"
                                type="number"
                                requiredIcon
                                placeholder="0.00"
                                error={errors.price?.message}
                                min={1}
                                {...register('price', { ...errorTypes.required('Price') })}
                            />
                            <Controller
                                name="categoryId"
                                control={control}
                                rules={{ ...errorTypes.required('Category') }}
                                render={({ field }) => (
                                    <FormField
                                        label="Category"
                                        name="categoryId"
                                        type="select"
                                        requiredIcon
                                        options={categoryOptions}
                                        value={field.value}
                                        onValueChange={(val) => {
                                            field.onChange(val);
                                            setValue('subCategoryId', '');
                                        }}
                                        error={errors.categoryId?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="subCategoryId"
                                control={control}
                                render={({ field }) => (
                                    <FormField
                                        label="Sub Category"
                                        name="subCategoryId"
                                        type="select"
                                        options={subCategoryOptions}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        error={errors.subCategoryId?.message}
                                    />
                                )}
                            />
                            <FormField
                                label="Stock Quantity"
                                name="stockQuantity"
                                type="number"
                                requiredIcon
                                placeholder="100"
                                min={1}
                                error={errors.stockQuantity?.message}
                                {...register('stockQuantity', { ...errorTypes.required('Stock Quantity') })}
                            />

                            <FormField
                                label="Additional Info"
                                name="additionalInfo"
                                type="text"
                                placeholder="e.g. 250g, XL, Cotton"
                                {...register('additionalInfo')}
                            />
                            <div className="space-y-1.5 pt-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-medium text-foreground flex items-center gap-1">
                                        Tags
                                        {/* <span className="text-red-500">*</span> */}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddTagModal(true)}
                                        className="p-1 rounded-md hover:bg-secondary/50 text-primary transition-colors border border-secondary/20 bg-secondary/10 cursor-pointer"
                                        title="Create New Tag"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                                <Controller
                                    name="tagIds"
                                    control={control}
                                    // rules={{ ...errorTypes.required('Tags') }}
                                    render={({ field }) => (
                                        <div className="space-y-1">
                                            <MultiSelect
                                                options={tagOptions}
                                                selected={field.value || []}
                                                onChange={field.onChange}
                                                onSearchChange={handleTagSearch}
                                                placeholder="Select tags..."
                                                className={errors.tagIds ? "border-red-500" : "rounded-2xl"}
                                                loading={loadingTags}
                                            />
                                            {/* {errors.tagIds && (
                                                <p className="text-[10px] font-medium text-red-500 animate-in fade-in slide-in-from-top-1">
                                                    {errors.tagIds.message}
                                                </p>
                                            )} */}
                                        </div>
                                    )}
                                />
                            </div>
                            <FormField
                                label="Description"
                                name="descriptionLong"
                                type="textarea"
                                requiredIcon
                                rows={3}
                                placeholder="Detailed description of the product..."
                                error={errors.descriptionLong?.message}
                                {...register('descriptionLong', { ...errorTypes.required('Description') })}
                            />


                        </div>

                    </div>

                    {/* ── Product Images ── */}
                    <div className="space-y-4 pt-2">
                        <Controller
                            name="images"
                            control={control}
                            rules={{ required: "At least one product image is required" }}
                            render={({ field }) => (
                                <MultiImageUploader
                                    label="Product Gallery"
                                    value={field.value}
                                    onChange={(newValue) => {
                                        if (isEditMode) {
                                            const removedImages = field.value.filter(item =>
                                                typeof item === 'string' && !newValue.includes(item)
                                            ) as string[];

                                            if (removedImages.length > 0) {
                                                const newDeletedIds = removedImages
                                                    .map(url => mediaMap[url])
                                                    .filter(id => id && !deletedMediaIds.includes(id));

                                                if (newDeletedIds.length > 0) {
                                                    setDeletedMediaIds(prev => [...prev, ...newDeletedIds]);
                                                }
                                            }
                                        }
                                        field.onChange(newValue);
                                    }}
                                    error={errors.images?.message}
                                    maxFiles={6}
                                    type=".jpg,.jpeg,.png,.webp,.mp4"
                                />
                            )}
                        />
                    </div>

                    {/* ── Addons Section ── */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between border-b pb-2">
                            <h3 className="text-sm font-bold flex items-center gap-2 text-primary">
                                <Plus className="w-4 h-4" />
                                Product Addons
                            </h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => append({ name: '', price: null, inventory: 0, isCounter: false, status: 'active', itemId: '' } as any)}
                            >
                                Add Addon
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {fields.map((field, index) => (
                                <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-secondary/5 p-5 rounded-2xl border border-secondary/20 relative animate-in fade-in slide-in-from-top-2">
                                    <div className="md:col-span-5 pb-2">
                                        <FormField
                                            label="Addon Name"
                                            name={`productAddons.${index}.name`}
                                            placeholder="e.g. Extra Cheese"
                                            {...register(`productAddons.${index}.name` as const, { ...errorTypes.required('Name') })}
                                        />
                                    </div>
                                    <div className="md:col-span-2 pb-2">
                                        <FormField
                                            label="Price"
                                            name={`productAddons.${index}.price`}
                                            type="number"
                                            placeholder="0.00"
                                            {...register(`productAddons.${index}.price` as const, { ...errorTypes.required('Price') })}
                                        />
                                    </div>
                                    <div className="md:col-span-2 pb-2">
                                        <FormField
                                            label="Inventory"
                                            name={`productAddons.${index}.inventory`}
                                            type="number"
                                            placeholder="1"
                                            {...register(`productAddons.${index}.inventory` as const, { ...errorTypes.required('Inventory') })}
                                        />
                                    </div>
                                    <div className="md:col-span-2 pb-2">
                                        <Controller
                                            name={`productAddons.${index}.status`}
                                            control={control}
                                            render={({ field }) => (
                                                <div className="flex flex-col gap-1.5">
                                                    <label className="text-xs font-medium text-foreground">Status</label>
                                                    <div className="flex items-center gap-2 h-10 px-1">
                                                        <Switch
                                                            checked={field.value === 'active'}
                                                            onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                                            className="data-[state=checked]:bg-green-500 shadow-sm"
                                                        />
                                                        <span className={`text-[10px] font-bold uppercase tracking-wider ${field.value === 'active' ? 'text-green-600' : 'text-muted-foreground'}`}>
                                                            {field.value}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div className="md:col-span-1 flex justify-center pb-3.5">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveAddon(index)}
                                            className="p-2.5 rounded-xl hover:bg-destructive/10 text-destructive transition-colors group border border-transparent hover:border-destructive/20 cursor-pointer"
                                            title="Remove Addon"
                                        >
                                            <Trash className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5">
                            <div className="space-y-0.5">
                                <Label className="text-sm font-medium">VAT Status</Label>
                                <p className="text-xs text-muted-foreground">Enable VAT</p>
                            </div>
                            <Controller
                                name="vatStatus"
                                control={control}
                                render={({ field }) => (
                                    <Switch
                                        checked={field.value === 'active'}
                                        onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* ── Actions ── */}
                    <div className="flex justify-end gap-3 pt-8 border-t">
                        <Button
                            type="button"
                            variant="outline"
                            className="px-8 h-11"
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="px-10 h-11 shadow-lg shadow-primary/20"
                            isLoading={isSubmitting}
                            loadingText={isEditMode ? "Updating Product..." : "Creating Product..."}
                        >
                            {isEditMode ? "Update Product" : "Create Product"}
                        </Button>
                    </div>
                </form>
            </div>

            {/* ── Modals ── */}
            <AddTag
                showAddModal={showAddTagModal}
                setShowAddModal={setShowAddTagModal}
                modalMode="create"
                onSuccess={() => {
                    const fetchTagsAfterAdd = async () => {
                        try {
                            const response = await callApi<ITagResponse>(`${API_URL + API_ENDPOINT.tags}?limit=100`, { method: 'GET' });
                            setTags(response?.data?.tags || []);
                        } catch (error) {
                            console.error("Error re-fetching tags:", error);
                        }
                    };
                    fetchTagsAfterAdd();
                }}
            />
        </Layout>
    );
};
